// src/components/OrderSuccessModal.js - Updated for single cart orders
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  Button,
  Box,
  styled,
  keyframes,
  Chip,
  Divider,
  Alert, // ADD THIS
} from "@mui/material";
import {
  CheckCircle,
  LocalShipping,
  Home,
  Celebration,
  Timeline,
  ShoppingBag,
  ShoppingCart,
  CardGiftcard, // ADD THIS
  MonetizationOn, // ADD THIS
  LocalOffer, // ADD THIS
} from "@mui/icons-material";

// Keyframe animations
const successPop = keyframes`
  0% { transform: scale(0) rotate(0deg); opacity: 0; }
  50% { transform: scale(1.2) rotate(180deg); opacity: 0.8; }
  100% { transform: scale(1) rotate(360deg); opacity: 1; }
`;

const whaleSwim = keyframes`
  0% { transform: translateX(-100px) translateY(0px) rotate(-10deg); }
  25% { transform: translateX(50px) translateY(-10px) rotate(5deg); }
  50% { transform: translateX(150px) translateY(0px) rotate(-5deg); }
  75% { transform: translateX(250px) translateY(-5px) rotate(10deg); }
  100% { transform: translateX(350px) translateY(0px) rotate(0deg); }
`;

const packageFloat = keyframes`
  0%, 100% { transform: translateY(0px) rotate(-2deg); }
  50% { transform: translateY(-8px) rotate(2deg); }
`;

const sparkleShine = keyframes`
  0%, 100% { opacity: 0; transform: scale(0) rotate(0deg); }
  50% { opacity: 1; transform: scale(1.5) rotate(180deg); }
`;

const bounceIn = keyframes`
  0% { transform: scale(0.3) translateY(50px); opacity: 0; }
  50% { transform: scale(1.1) translateY(-10px); opacity: 0.8; }
  100% { transform: scale(1) translateY(0px); opacity: 1; }
`;

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(76, 175, 80, 0.3); }
  50% { box-shadow: 0 0 40px rgba(76, 175, 80, 0.6); }
`;

// Styled components
const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    background: "rgba(255, 255, 255, 0.95)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    borderRadius: "24px",
    boxShadow: "0 8px 32px rgba(11, 28, 72, 0.37)",
    maxWidth: "500px",
    width: "90%",
    overflow: "visible",
  },
}));

const SuccessIcon = styled(Box)({
  fontSize: "80px",
  color: "#00BFFF",
  animation: `${successPop} 1s ease-out`,
  marginBottom: "20px",
  filter: "drop-shadow(0 10px 20px rgba(76, 175, 80, 0.3))",
});

const DeliveryContainer = styled(Box)({
  position: "relative",
  height: "120px",
  background: "linear-gradient(135deg, #F0F8FF 0%, #E6F0FA 100%)",
  borderRadius: "16px",
  margin: "20px 0",
  overflow: "hidden",
  border: "1px solid rgba(0, 114, 206, 0.2)",
});

const WhaleDelivery = styled("img")({
  position: "absolute",
  top: "20px",
  left: "-80px",
  width: "60px",
  height: "60px",
  animation: `${whaleSwim} 3s ease-in-out infinite`,
  filter: "drop-shadow(0 5px 15px rgba(11, 28, 72, 0.3))",
});

const DeliveryPackage = styled(Box)({
  position: "absolute",
  top: "15px",
  right: "20px",
  fontSize: "35px",
  animation: `${packageFloat} 2s ease-in-out infinite`,
  filter: "drop-shadow(0 5px 10px rgba(0, 0, 0, 0.1))",
});

const Sparkle = styled(Box)(({ delay, left, top }) => ({
  position: "absolute",
  left: `${left}%`,
  top: `${top}%`,
  fontSize: "20px",
  animation: `${sparkleShine} 2s ease-in-out infinite ${delay}s`,
  color: "#ffd700",
}));

const OrderDetailsCard = styled(Box)({
  background: "rgba(255, 255, 255, 0.6)",
  backdropFilter: "blur(10px)",
  border: "1px solid rgba(255, 255, 255, 0.3)",
  borderRadius: "12px",
  padding: "15px",
  margin: "15px 0",
});

const CelebrationButton = styled(Button)({
  background: "linear-gradient(45deg, #00BFFF, #60A5FA)",
  color: "white",
  fontWeight: "bold",
  borderRadius: "12px",
  padding: "12px 30px",
  fontSize: "1.1rem",
  boxShadow: "0 4px 20px rgba(76, 175, 80, 0.3)",
  "&:hover": {
    background: "linear-gradient(45deg, #388e3c, #00BFFF)",
    transform: "translateY(-2px)",
    boxShadow: "0 6px 25px rgba(76, 175, 80, 0.4)",
  },
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  animation: `${bounceIn} 1s ease-out 2s both`,
});

const OrderSuccessModal = ({
  open,
  onClose,
  orderData,
  primaryBlue,
  onContinueShopping,
}) => {
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    if (open) {
      setCurrentStep(1);
    }
  }, [open]);

  const handleContinueShopping = () => {
    // Call the continue shopping function passed from parent
    if (onContinueShopping) {
      onContinueShopping();
    } else {
      // Fallback to just closing the modal
      onClose();
    }
  };

  const sparkles = Array.from({ length: 6 }, (_, i) => (
    <Sparkle
      key={i}
      delay={i * 0.3}
      left={Math.random() * 80 + 10}
      top={Math.random() * 60 + 20}
    >
      ✨
    </Sparkle>
  ));

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(price);
  };

  // Function to generate short, readable order IDs
  // Function to generate short, readable order IDs
  const formatOrderId = (longId) => {
    if (!longId) return "UNKNOWN";

    console.log("Formatting Order ID:", longId); // Debug log

    if (longId.includes("CART-")) {
      const parts = longId.split("-");
      if (parts.length >= 2) {
        const timestamp = parts[1];
        const shortTimestamp = timestamp.slice(-6);
        return `CART${shortTimestamp}`;
      }
    }

    // Fallback: use last 8 characters
    const shortId = longId.slice(-8).toUpperCase();
    return `#${shortId}`;
  };

  return (
    <StyledDialog
      open={open}
      onClose={() => {}} // Disable closing by clicking outside
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
    >
      <DialogContent sx={{ textAlign: "center", p: 4, position: "relative" }}>
        {sparkles}

        <SuccessIcon>🎉</SuccessIcon>

        <Typography
          variant="h4"
          sx={{
            fontWeight: "bold",
            background: "linear-gradient(45deg, #00BFFF, #60A5FA)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            marginBottom: 1,
          }}
        >
          Order Placed Successfully! 🎊
        </Typography>

        <Typography
          variant="h6"
          sx={{
            color: primaryBlue[600],
            marginBottom: 3,
            fontWeight: 500,
          }}
        >
          Thank you for shopping with InfinityWhale! 🐋
        </Typography>

        {/* Delivery Animation */}
        <DeliveryContainer>
          <WhaleDelivery
            src="/infinitywhalelogo.png"
            alt="InfinityWhale Logo"
          />
          <DeliveryPackage>📦</DeliveryPackage>
          <Box
            sx={{
              position: "absolute",
              bottom: 10,
              left: "50%",
              transform: "translateX(-50%)",
              fontSize: "12px",
              color: primaryBlue[600],
              fontWeight: 600,
            }}
          >
            🌊 InfinityWhale Express Delivery 🌊
          </Box>
        </DeliveryContainer>

        {/* Order Details */}
        {orderData && (
          <OrderDetailsCard>
            <Typography
              variant="h6"
              sx={{ color: primaryBlue[700], fontWeight: 600, mb: 2 }}
            >
              📋 Order Summary
            </Typography>

            {/* Single Order ID Display */}
            {orderData.orderIds && orderData.orderIds.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Order ID:
                </Typography>
                <Chip
                  label={formatOrderId(orderData.orderIds[0])}
                  size="medium"
                  sx={{
                    bgcolor: primaryBlue[100],
                    color: primaryBlue[700],
                    fontWeight: 700,
                    fontSize: "1rem",
                    height: "32px",
                  }}
                />
              </Box>
            )}

            {/* Order Content Summary */}
            {orderData.orderCounts && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Order Contains:
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    flexWrap: "wrap",
                    justifyContent: "center",
                  }}
                >
                  {orderData.orderCounts.totalPackages > 0 && (
                    <Chip
                      icon={<ShoppingBag />}
                      label={`${orderData.orderCounts.totalPackages} Package${
                        orderData.orderCounts.totalPackages > 1 ? "s" : ""
                      }`}
                      size="small"
                      sx={{
                        bgcolor: "rgba(76, 175, 80, 0.1)",
                        color: "#2e7d32",
                        fontWeight: 600,
                      }}
                    />
                  )}
                  {orderData.orderCounts.totalItems > 0 && (
                    <Chip
                      icon={<ShoppingCart />}
                      label={`${orderData.orderCounts.totalItems} Item${
                        orderData.orderCounts.totalItems > 1 ? "s" : ""
                      }`}
                      size="small"
                      sx={{
                        bgcolor: "rgba(0, 114, 206, 0.1)",
                        color: primaryBlue[700],
                        fontWeight: 600,
                      }}
                    />
                  )}
                </Box>
              </Box>
            )}

            {/* NEW: Applied Discounts Display */}
            {orderData.appliedDiscounts &&
              (orderData.appliedDiscounts.usedCashback ||
                orderData.appliedDiscounts.usedCommission) && (
                <Alert
                  severity="success"
                  sx={{
                    mt: 2,
                    mb: 2,
                    background:
                      "linear-gradient(135deg, rgba(76, 175, 80, 0.1), rgba(102, 187, 106, 0.05))",
                    border: "1px solid rgba(76, 175, 80, 0.3)",
                  }}
                  icon={<LocalOffer sx={{ color: "#00BFFF" }} />}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: "#2e7d32", mb: 1 }}
                  >
                    🎉 You saved ₹
                    {(orderData.appliedDiscounts.totalDiscount || 0).toFixed(2)}{" "}
                    with applied discounts!
                  </Typography>

                  <Box sx={{ ml: 2 }}>
                    {orderData.appliedDiscounts.usedCashback && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
                      >
                        <CardGiftcard
                          sx={{ color: "#00BFFF", mr: 1, fontSize: 16 }}
                        />
                        <Typography variant="body2" sx={{ color: "#2e7d32" }}>
                          Cashback Applied: ₹
                          {orderData.appliedDiscounts.cashbackDiscount.toFixed(
                            2
                          )}
                        </Typography>
                      </Box>
                    )}

                    {orderData.appliedDiscounts.usedCommission && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
                      >
                        <MonetizationOn
                          sx={{ color: "#1E40AF", mr: 1, fontSize: 16 }}
                        />
                        <Typography variant="body2" sx={{ color: "#2e7d32" }}>
                          Points Applied: ₹
                          {orderData.appliedDiscounts.commissionDiscount.toFixed(
                            2
                          )}
                          {orderData.appliedDiscounts.commissionAdminFee >
                            0 && (
                            <span
                              style={{ fontSize: "0.8em", color: "#D4AF37" }}
                            >
                              {" "}
                              (₹
                              {orderData.appliedDiscounts.commissionAdminFee.toFixed(
                                2
                              )}{" "}
                              admin fee deducted)
                            </span>
                          )}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Typography
                    variant="caption"
                    sx={{ color: "#2e7d32", fontStyle: "italic" }}
                  >
                    These amounts have been deducted from your wallet balances.
                  </Typography>
                </Alert>
              )}

            <Divider sx={{ my: 2 }} />

            {/* Price Breakdown */}
            <Box sx={{ textAlign: "left" }}>
              {/* Show original subtotal if discounts were applied */}
              {orderData.appliedDiscounts &&
                orderData.appliedDiscounts.totalDiscount > 0 && (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Subtotal:
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatPrice(
                          (orderData.totalAmount || 0) +
                            (orderData.appliedDiscounts.totalDiscount || 0)
                        )}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ color: "#00BFFF", fontWeight: 600 }}
                      >
                        Total Discounts:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "#00BFFF", fontWeight: 600 }}
                      >
                        -
                        {formatPrice(
                          orderData.appliedDiscounts.totalDiscount || 0
                        )}
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 1 }} />
                  </>
                )}

              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography variant="body1" fontWeight={600}>
                  Final Amount Paid:
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ color: "#00BFFF", fontWeight: 700 }}
                >
                  {formatPrice(orderData.totalAmount)}
                </Typography>
              </Box>
            </Box>

            <Typography
              variant="body2"
              color="success.main"
              fontWeight={500}
              sx={{ mt: 2 }}
            >
              ✅ Payment successful — your order is confirmed
            </Typography>
          </OrderDetailsCard>
        )}

        <Box
          sx={{
            background:
              "linear-gradient(135deg, rgba(76, 175, 80, 0.1), rgba(102, 187, 106, 0.1))",
            borderRadius: "12px",
            padding: "15px",
            margin: "20px 0",
            border: "1px solid rgba(76, 175, 80, 0.2)",
          }}
        >
          <Typography
            variant="body1"
            sx={{ color: "#2e7d32", fontWeight: 500 }}
          >
            🎯 <strong>What's Next?</strong>
          </Typography>
          <Typography variant="body2" sx={{ color: "#2e7d32", mt: 1 }}>
            • Your payment was successful and your order is confirmed
            <br />
            • You'll receive updates via email and notifications
            <br />• Items will be shipped to your provided address
            {orderData?.appliedDiscounts?.totalDiscount > 0 && (
              <Box
                sx={{
                  background:
                    "linear-gradient(135deg, rgba(255, 193, 7, 0.1), rgba(255, 235, 59, 0.05))",
                  borderRadius: "12px",
                  padding: "12px",
                  margin: "15px 0",
                  border: "1px solid rgba(255, 193, 7, 0.3)",
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ color: "#f57f17", fontWeight: 600 }}
                >
                  {orderData.appliedDiscounts.totalDiscount >= 100
                    ? "🌟 Amazing! You're a savings superstar!"
                    : orderData.appliedDiscounts.totalDiscount >= 50
                    ? "💫 Great job on those savings!"
                    : "✨ Every rupee saved counts!"}
                </Typography>
                <Typography variant="body2" sx={{ color: "#f57f17", mt: 0.5 }}>
                  Keep shopping and referring friends to earn more cashback and
                  points!
                </Typography>
              </Box>
            )}
            {orderData?.orderCounts?.totalPackages > 0 && (
              <Alert
                severity="info"
                sx={{
                  mb: 2,
                  background:
                    "linear-gradient(135deg, rgba(0, 114, 206, 0.1), rgba(100, 181, 246, 0.05))",
                  border: "1px solid rgba(0, 114, 206, 0.3)",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: primaryBlue[700] }}
                >
                  🎁 First-Time Package Purchase Bonus!
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: primaryBlue[600], mt: 0.5 }}
                >
                  If this is your first package purchase, first-time cashback
                  is applied when eligible — separate from any discounts you
                  just applied!
                </Typography>
              </Alert>
            )}
          </Typography>
        </Box>

        <CelebrationButton
          variant="contained"
          onClick={handleContinueShopping}
          startIcon={<Celebration />}
          fullWidth
        >
          Continue Shopping! 🛍️
        </CelebrationButton>
      </DialogContent>
    </StyledDialog>
  );
};

export default OrderSuccessModal;
